import { create } from 'zustand';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface PWAState {
  // Installation
  installPrompt: BeforeInstallPromptEvent | null;
  isInstalled: boolean;
  canInstall: boolean;

  // Offline status
  isOffline: boolean;

  // Update available
  updateAvailable: boolean;
  swRegistration: ServiceWorkerRegistration | null;

  // Mobile sidebar state
  isMobileSidebarOpen: boolean;

  // Actions
  setInstallPrompt: (prompt: BeforeInstallPromptEvent | null) => void;
  setIsInstalled: (installed: boolean) => void;
  setCanInstall: (canInstall: boolean) => void;
  setIsOffline: (offline: boolean) => void;
  setUpdateAvailable: (available: boolean, registration?: ServiceWorkerRegistration) => void;
  setMobileSidebarOpen: (open: boolean) => void;
  toggleMobileSidebar: () => void;

  // PWA Actions
  promptInstall: () => Promise<boolean>;
  applyUpdate: () => void;
}

export const usePWAStore = create<PWAState>((set, get) => ({
  installPrompt: null,
  isInstalled: false,
  canInstall: false,
  isOffline: !navigator.onLine,
  updateAvailable: false,
  swRegistration: null,
  isMobileSidebarOpen: false,

  setInstallPrompt: (prompt) => set({ installPrompt: prompt, canInstall: !!prompt }),
  setIsInstalled: (installed) => set({ isInstalled: installed, canInstall: !installed }),
  setCanInstall: (canInstall) => set({ canInstall }),
  setIsOffline: (offline) => set({ isOffline: offline }),
  setUpdateAvailable: (available, registration) =>
    set({ updateAvailable: available, swRegistration: registration || null }),
  setMobileSidebarOpen: (open) => set({ isMobileSidebarOpen: open }),
  toggleMobileSidebar: () => set((state) => ({ isMobileSidebarOpen: !state.isMobileSidebarOpen })),

  promptInstall: async () => {
    const { installPrompt } = get();
    if (!installPrompt) return false;

    try {
      await installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;

      if (outcome === 'accepted') {
        set({ isInstalled: true, canInstall: false, installPrompt: null });
        return true;
      }
    } catch (error) {
      console.error('Installation prompt error:', error);
    }

    return false;
  },

  applyUpdate: () => {
    const { swRegistration } = get();
    if (swRegistration?.waiting) {
      swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
      window.location.reload();
    }
  },
}));

// Initialize PWA event listeners
if (typeof window !== 'undefined') {
  // Listen for beforeinstallprompt event
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    usePWAStore.getState().setInstallPrompt(e as BeforeInstallPromptEvent);
  });

  // Listen for appinstalled event
  window.addEventListener('appinstalled', () => {
    usePWAStore.getState().setIsInstalled(true);
    usePWAStore.getState().setInstallPrompt(null);
  });

  // Check if already installed (display mode)
  if (window.matchMedia('(display-mode: standalone)').matches) {
    usePWAStore.getState().setIsInstalled(true);
  }

  // iOS standalone check
  if ((window.navigator as any).standalone === true) {
    usePWAStore.getState().setIsInstalled(true);
  }
}
